import {proxy, ref} from "valtio";

import {type Quote, submitInquiryBooking} from "@/entities/booking";
import {PricingSummary, type PricingSummaryInstance} from "@/entities/booking/model/pricing-summary.model";

import {Field, type FieldInstance} from "@/shared/models/form/fields";
import {EmailValidator, MaxLengthValidator, MinLengthValidator, RequiredValidator} from "@/shared/models/form/fields/validators";
import {FormSubmitFeedbackModel, type FormSubmitFeedbackModelInstance} from "@/shared/models/form/form-submit-feedback";
import {PhoneField, type PhoneFieldInstance} from "@/shared/models/form/phone";
import {QueryState, type QueryStateInstance} from "@/shared/services";
import {getSegmentAnonymousId} from "@/shared/services/analytics/analytics.emitters";
import {InquiryFormAnalytics, type InquiryFormAnalyticsInstance} from "@/shared/services/analytics/inquiry-form.analytics";

export interface InquiryFormPayload {
    unitId: string;
    unitName?: string;
}

export interface InquiryPricingConfig {
    quote: Quote;
    nights: number;
}

/** Passed to `setBookingInfo`; `pricing` always refreshes the pricing summary before storing booking + open analytics. */
export type InquirySetBookingInfoParams = InquiryBookingInfo & {
    pricing: InquiryPricingConfig;
};

export interface InquiryBookingInfo {
    checkIn: string | null;
    checkOut: string | null;
    checkInTime?: string;
    checkOutTime?: string;
    adults: number;
    children: number;
    pets: number;
    unitImage?: string;
    unitLocation?: string;
    rating?: number;
    reviewsCount?: number;
}

class InquiryFormClass {
    readonly unitId: string;
    readonly unitName: string;

    firstName: FieldInstance;
    lastName: FieldInstance;
    email: FieldInstance;
    phone: PhoneFieldInstance;
    message: FieldInstance;

    readonly submitQuery: QueryStateInstance<void>;
    readonly submitFeedback: FormSubmitFeedbackModelInstance;
    pricing: PricingSummaryInstance | null = null;
    bookingInfo: InquiryBookingInfo | null = null;

    private readonly analytics: InquiryFormAnalyticsInstance;

    constructor(payload: InquiryFormPayload) {
        this.unitId = payload.unitId;
        this.unitName = payload.unitName ?? "";
        this.analytics = InquiryFormAnalytics();

        this.firstName = Field({
            validators: [new RequiredValidator(), new MinLengthValidator(2), new MaxLengthValidator(64)],
        });
        this.lastName = Field({
            validators: [new RequiredValidator(), new MinLengthValidator(2), new MaxLengthValidator(64)],
        });
        this.email = Field({
            validators: [new RequiredValidator(), new EmailValidator(), new MaxLengthValidator(64)],
        });
        this.phone = PhoneField({
            validators: [new RequiredValidator()],
        });
        this.message = Field({
            validators: [new MaxLengthValidator(1000)],
        });

        this.submitFeedback = FormSubmitFeedbackModel();

        this.submitQuery = QueryState<void>(async (_signal) => {
            const info = this.bookingInfo;
            if (!info?.checkIn || !info?.checkOut) {
                throw new Error("Booking dates are required");
            }

            const result = await submitInquiryBooking({
                quote: {
                    unitId: this.unitId,
                    checkIn: info.checkIn,
                    checkOut: info.checkOut,
                    adults: info.adults,
                    children: info.children,
                    pets: info.pets,
                },
                contactInfo: {
                    firstName: this.firstName.value,
                    lastName: this.lastName.value,
                    email: this.email.value,
                    phone: this.phone.formattedInternational?.replace(/\s/g, "") ?? "",
                },
                message: this.message.value,
                analytics: {
                    anonymousId: getSegmentAnonymousId(),
                },
            });

            if (!result.success) {
                this.analytics.trackSubmitFromBooking(this.unitId, this.bookingInfo, false, result.error.errorCode);
                throw new Error(result.error.message);
            }

            this.analytics.trackSubmitFromBooking(this.unitId, this.bookingInfo, true);

            this.submitFeedback.setSuccess({
                title: "booking.inquirySuccessTitle",
                message: "booking.inquirySuccessMessage",
            });
        });
    }

    validate(): boolean {
        const namedFields = [
            ["firstName", this.firstName],
            ["lastName", this.lastName],
            ["email", this.email],
            ["phone", this.phone],
            ["message", this.message],
        ] as const;

        for (const [, field] of namedFields) {
            field.validate();
        }

        const invalidKeys = namedFields.filter(([, field]) => !field.isValid).map(([key]) => key);
        if (invalidKeys.length > 0) {
            this.analytics.trackValidationError({
                unit_id: this.unitId,
                fields: invalidKeys.join(","),
            });
            const firstInvalid = namedFields.find(([, field]) => !field.isValid);
            firstInvalid?.[1].events.emit("scrollIntoView");
            return false;
        }
        return true;
    }

    setBookingInfo(params: InquirySetBookingInfoParams): void {
        const {pricing, ...rest} = params;
        this.pricing = ref(PricingSummary(pricing)) as PricingSummaryInstance;
        this.bookingInfo = rest;

        this.analytics.trackOpen({
            unit_id: this.unitId,
            check_in: rest.checkIn,
            check_out: rest.checkOut,
            adults: rest.adults,
            children: rest.children,
            pets: rest.pets,
            total_price: pricing.quote.total,
        });
    }

    reset(): void {
        this.firstName.clear();
        this.lastName.clear();
        this.email.clear();
        this.phone.clear();
        this.message.clear();
        this.submitQuery.reset();
        this.submitFeedback.clear();
    }

    async submit(): Promise<void> {
        if (!this.validate()) return;
        this.submitFeedback.clear();
        try {
            await this.submitQuery.execute();
        } catch {
            if (this.submitQuery.isRejected) {
                const detail = this.submitQuery.errorMessage?.trim();
                if (detail && detail.length > 0) {
                    this.submitFeedback.setError({message: detail});
                } else {
                    this.submitFeedback.setGenericError();
                }
            }
        }
    }
}

export type InquiryFormInstance = InquiryFormClass;

export function InquiryForm(payload: InquiryFormPayload): InquiryFormInstance {
    return proxy(new InquiryFormClass(payload));
}
